import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Divider,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  ListItemText,
  List,
  ListItem,
  ListItemIcon,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Receipt as ReceiptIcon,
  Print as PrintIcon,
  KeyboardReturn as ReturnIcon,
  AttachMoney as MoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  CompareArrows as CompareArrowsIcon,
  Payment as PaymentIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import * as invoiceService from '../../services/invoiceService';
import { IInvoicePage, IInvoice } from '../../types/invoice';
import { IInvoiceItemExtended, IInvoiceCreateExtended } from '../../types/invoiceExtended';
import { IPaymentMethodUsage } from '../../types/paymentMethod';
import PaymentMethodsSection from './PaymentMethodsSection';

interface ImportInvoiceReturnFormProps {
  pageId: string;
  invoiceId?: string;
  page: IInvoicePage;
  onSave: (data: IInvoiceCreateExtended) => Promise<any>;
  onCancel: () => void;
  onPrint: () => void;
  showPrintButton: boolean;
  savedInvoiceId: string | null;
}

const ImportInvoiceReturnForm: React.FC<ImportInvoiceReturnFormProps> = ({
  pageId,
  invoiceId,
  page,
  onSave,
  onCancel,
  onPrint,
  showPrintButton,
  savedInvoiceId
}) => {
  const navigate = useNavigate();
  const { activeBranch, user } = useAuth();
  const isEditing = invoiceId && invoiceId !== 'new';

  // State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourceInvoiceNumber, setSourceInvoiceNumber] = useState('');
  const [sourceInvoice, setSourceInvoice] = useState<IInvoice | null>(null);
  const [sourceInvoiceItems, setSourceInvoiceItems] = useState<IInvoiceItemExtended[]>([]);
  const [returnItems, setReturnItems] = useState<IInvoiceItemExtended[]>([]);
  const [useAlternativePayment, setUseAlternativePayment] = useState(false);
  const [cashPaymentAmount, setCashPaymentAmount] = useState<number>(0);
  
  // State for payment methods dialog
  const [showPaymentMethodsDialog, setShowPaymentMethodsDialog] = useState(false);
  const [importedPaymentMethods, setImportedPaymentMethods] = useState<IPaymentMethodUsage[]>([]);

  // Form state
  const [formData, setFormData] = useState<IInvoiceCreateExtended>({
    pageId: pageId || '',
    invoiceNumber: '',
    date: (() => {
      const now = new Date();
      const riyadhTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Riyadh' }));
      const year = riyadhTime.getFullYear();
      const month = String(riyadhTime.getMonth() + 1).padStart(2, '0');
      const day = String(riyadhTime.getDate()).padStart(2, '0');
      const hours = String(riyadhTime.getHours()).padStart(2, '0');
      const minutes = String(riyadhTime.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    })(),
    status: 'draft',
    items: [],
    subtotal: 0,
    tax: 0,
    discount: 0,
    total: 0,
    notes: '',
    branchId: activeBranch?._id || '',
    paymentMethods: []
  });

  // Fetch existing invoice data if editing
  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!isEditing || !invoiceId) return;
      
      try {
        setLoading(true);
        const invoiceData = await invoiceService.getInvoiceById(invoiceId);
        
        // Load the source invoice if there's a sourceInvoiceId
        if (invoiceData.sourceInvoiceId) {
          // استخدام رقم الفاتورة المحفوظ إذا كان متوفراً، وإلا استخدام المعرف كـ fallback
          const invoiceNumberToUse = invoiceData.sourceInvoiceNumber || invoiceData.sourceInvoiceId;
          setSourceInvoiceNumber(invoiceNumberToUse);
          await fetchSourceInvoice(invoiceNumberToUse);
        }
        
        // Load cash payment amount if exists
        if (invoiceData.cashPaidAmount && invoiceData.cashPaidAmount > 0) {
          setCashPaymentAmount(invoiceData.cashPaidAmount);
          setUseAlternativePayment(true);
        }
        
        setFormData({
          pageId,
          invoiceNumber: invoiceData.invoiceNumber,
          date: new Date(invoiceData.date).toISOString().slice(0, 16),
          status: invoiceData.status,
          items: invoiceData.items,
          subtotal: invoiceData.subtotal,
          tax: invoiceData.tax,
          discount: invoiceData.discount,
          total: invoiceData.total,
          notes: invoiceData.notes || '',
          branchId: invoiceData.branchId,
          paymentMethods: invoiceData.paymentMethods || []
        });
        
        // Set return items from the loaded invoice
        setReturnItems(invoiceData.items as IInvoiceItemExtended[]);
        
        // Check if this invoice was marked for alternative payment
        setUseAlternativePayment(invoiceData.status === 'draft' && 
                                (!invoiceData.paymentMethods || invoiceData.paymentMethods.length === 0));
        
      } catch (err: any) {
        console.error('Error loading invoice:', err);
        setError(`حدث خطأ أثناء تحميل الفاتورة: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInvoiceData();
  }, [isEditing, invoiceId, pageId]);

  // Generate new invoice number for new invoices
  useEffect(() => {
    if (!isEditing) {
      setFormData(prev => ({
        ...prev,
        invoiceNumber: `INVRET-${Date.now().toString().substring(9)}`
      }));
    }
  }, [isEditing]);

  // Fetch source invoice by invoice number
  const fetchSourceInvoice = async (invoiceNumber: string) => {
    if (!invoiceNumber.trim()) {
      setError('الرجاء إدخال رقم الفاتورة');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Call API to search for invoice by number
      const response = await invoiceService.searchInvoiceByNumber(invoiceNumber);
      if (!response || !response.data || !response.data.invoice) {
        throw new Error('لم يتم العثور على فاتورة بهذا الرقم');
      }
      
      const invoice = response.data.invoice;
      console.log('🔍 الفاتورة المستوردة:', invoice);
      console.log('🔍 طرق الدفع في الفاتورة:', invoice.paymentMethods);
      setSourceInvoice(invoice);
      
      // Convert invoice items to extended format if needed
      // مهم جداً: يجب توزيع الخصم الكلي على الفاتورة على المنتجات (باستثناء المنتجات الخدمية)
      
      // دالة مساعدة لحساب مبلغ الخصم الفعلي بناءً على نوع الخصم
      const getDiscountAmount = (discount: number, discountType: string, baseAmount: number): number => {
        if (!discount || discount === 0) return 0;
        if (discountType === 'percentage') {
          return baseAmount * (discount / 100);
        }
        return discount; // fixed amount
      };
      
      const invoiceSubtotal = invoice.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
      const invoiceSubtotalNonService = invoice.items.reduce((sum, item) => {
        return (item as any).isServiceProduct ? sum : sum + (item.price * item.quantity);
      }, 0);
      
      // تحديد نوع الخصم من الفاتورة نفسها
      const discountType = invoice.discountType || 'fixed';
      
      // حساب الخصم الكلي بناءً على invoiceSubtotalNonService وليس invoice.subtotal
      // هذا يضمن أن الخصم يُحسب على نفس القاعدة المستخدمة لتوزيعه على المنتجات
      const invoiceDiscount = getDiscountAmount(invoice.discount || 0, discountType, invoiceSubtotalNonService || 0);
      
      const extendedItems: IInvoiceItemExtended[] = invoice.items.map(item => {
        // حساب خصم المنتج كقيمة ثابتة (تحويل من نسبة إلى مبلغ إذا لزم الأمر)
        const itemTotalBeforeDiscount = item.price * item.quantity;
        const itemDiscountFixed = getDiscountAmount(item.discount || 0, item.discountType || 'fixed', itemTotalBeforeDiscount);
        
        // حساب حصة المنتج من الخصم الكلي
        let itemShareOfInvoiceDiscount = 0;
        
        if (invoiceDiscount > 0 && invoiceSubtotalNonService > 0 && !(item as any).isServiceProduct) {
          // حصة المنتج من الخصم الكلي = (سعر المنتج × الكمية ÷ المجموع الفرعي غير الخدمي) × الخصم الكلي
          const itemTotal = item.price * item.quantity;
          const itemRatio = itemTotal / invoiceSubtotalNonService;
          itemShareOfInvoiceDiscount = invoiceDiscount * itemRatio;
        }
        
        // إجمالي خصم المنتج = خصم المنتج الخاص به + حصته من الخصم الكلي
        const totalItemDiscount = itemDiscountFixed + itemShareOfInvoiceDiscount;
        
        return {
          ...item,
          discount: totalItemDiscount, // تحديث الخصم ليشمل حصة المنتج من الخصم الكلي (كقيمة ثابتة)
          discountType: 'fixed', // تحويل إلى ثابت بعد الحساب
          availableQuantity: 0, // Default, not needed for return
          barcodeId: (item as any).barcodeId || '',   // Preserve barcodeId if it exists
          barcode: item.barcode || '',       // Preserve barcode if it exists
          variantId: (item as any).variantId || ''    // Preserve variantId if it exists
        };
      });
      
      setSourceInvoiceItems(extendedItems);
      
      // استيراد طرق الدفع من الفاتورة الأصلية
      console.log('🔍 فحص طرق الدفع...');
      console.log('🔍 هل يوجد paymentMethods؟', !!invoice.paymentMethods);
      console.log('🔍 عدد طرق الدفع:', invoice.paymentMethods?.length || 0);
      
      if (invoice.paymentMethods && invoice.paymentMethods.length > 0) {
        console.log('✅ تم العثور على طرق دفع في الفاتورة الأصلية:', invoice.paymentMethods);
        setImportedPaymentMethods(invoice.paymentMethods);
        
        // نسخ طرق الدفع إلى الفاتورة الجديدة
        setFormData(prev => ({
          ...prev,
          paymentMethods: invoice.paymentMethods || []
        }));
        
        // إظهار النافذة المنبثقة فقط إذا كانت هناك طرق دفع
        console.log('🔔 محاولة إظهار النافذة المنبثقة...');
        setShowPaymentMethodsDialog(true);
        console.log('🔔 تم تعيين showPaymentMethodsDialog إلى true');
      } else {
        console.log('⚠️ لا توجد طرق دفع في الفاتورة الأصلية');
        // لا تظهر النافذة إذا لم تكن هناك طرق دفع
        setImportedPaymentMethods([]);
      }
      
      setError(null);
    } catch (err: any) {
      console.error('Error fetching source invoice:', err);
      setSourceInvoice(null);
      setSourceInvoiceItems([]);
      setError(err.message || 'حدث خطأ أثناء البحث عن الفاتورة');
    } finally {
      setLoading(false);
    }
  };

  // Add item to return list with enhanced validation
  const addItemToReturn = async (item: IInvoiceItemExtended) => {
    try {
      setError(null);
      
      // منع استرجاع المنتجات الخدمية لغير مدير النظام
      if (item.isServiceProduct && !user?.isSystemAdmin) {
        setError('لا يمكن استرجاع المنتجات الخدمية إلا لمدير النظام');
        return;
      }
      
      // Check if this exact row (same product + barcode + price + discount) is already in return list
      // Use both price AND discount to distinguish between rows with same product but different discounts
      const existingIndex = returnItems.findIndex(i =>
        i.productId === item.productId &&
        i.barcode === item.barcode &&
        i.price === item.price &&
        i.discount === item.discount
      );
      
      // Find the exact source row that matches (same product + barcode + price + discount)
      const matchingSourceItems = sourceInvoiceItems.filter(i =>
        i.productId === item.productId &&
        i.barcode === item.barcode &&
        i.price === item.price &&
        i.discount === item.discount
      );
      
      if (!matchingSourceItems || matchingSourceItems.length === 0) {
        setError('المنتج غير موجود في الفاتورة الأصلية');
        return;
      }

      // Use first matching item for price/discount calculations
      const sourceItem = matchingSourceItems[0];
      // Sum quantities from ALL matching rows with same price
      const maxQtyForThisRow = matchingSourceItems.reduce((sum, i) => sum + i.quantity, 0);
      
      // Check available quantity by calling the backend validation
      if (sourceInvoice) {
        try {
          // حساب مجموع الكميات المطلوبة لنفس المنتج بنفس السعر في قائمة المرتجعات الحالية
          const totalRequestedForProductAtPrice = returnItems
            .filter(i => i.productId === item.productId && i.barcode === item.barcode && i.price === item.price)
            .reduce((sum, i) => sum + i.quantity, 0);

          // الكمية المطلوبة الجديدة = الكمية الحالية في الشاشة + 1
          // عند التعديل: الخادم يستثني الفاتورة الحالية من المرتجعات السابقة،
          // لذا نرسل الكمية الإجمالية الجديدة (ما في الشاشة + 1) وهي تمثل الكمية الكاملة للفاتورة بعد التعديل
          const newRequestedQuantity = totalRequestedForProductAtPrice + 1;
          
          const response = await fetch('/api/invoices/validate-return-quantity', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              sourceInvoiceNumber: sourceInvoice.invoiceNumber,
              productId: item.productId,
              barcode: item.barcode,
              requestedQuantity: newRequestedQuantity,
              // تمرير السعر للتحقق على مستوى السطر المحدد (يمنع إرجاع نفس السطر مرتين بفواتير مختلفة)
              price: item.price,
              // تمرير معرف الفاتورة الحالية عند التعديل لاستثنائها من حساب المرتجعات السابقة
              currentInvoiceId: isEditing ? invoiceId : undefined
            })
          });
          
          const validationResult = await response.json();
          
          if (!validationResult.success) {
            setError(validationResult.message);
            return;
          }
        } catch (validationError) {
          console.error('خطأ في التحقق من الكمية المتاحة:', validationError);
          // Continue with local validation as fallback
        }
      }
      
      if (existingIndex >= 0) {
        // If this exact row exists, increment quantity if possible
        const newReturnItems = [...returnItems];
        const currentQty = newReturnItems[existingIndex].quantity;
        
        if (currentQty < maxQtyForThisRow) {
          // حساب السعر الفعلي للوحدة بعد توزيع الخصم
          // الخصم الآن دائماً قيمة ثابتة بعد التحويل في fetchSourceInvoice
          const totalBeforeDiscount = sourceItem.price * sourceItem.quantity;
          const totalAfterDiscount = totalBeforeDiscount - (sourceItem.discount || 0);
          const pricePerUnit = totalAfterDiscount / sourceItem.quantity;
          
          newReturnItems[existingIndex] = {
            ...newReturnItems[existingIndex],
            quantity: currentQty + 1,
            total: (currentQty + 1) * pricePerUnit
          };
          setReturnItems(newReturnItems);
          calculateTotals(newReturnItems);
        } else {
          setError('لا يمكن إرجاع كمية أكثر من الكمية الموجودة في هذا السطر من الفاتورة الأصلية');
        }
      } else {
        // Add new item with quantity 1 (each row is added separately)
        // حساب السعر الفعلي للوحدة بعد توزيع الخصم
        // الخصم الآن دائماً قيمة ثابتة بعد التحويل في fetchSourceInvoice
        const totalBeforeDiscount = item.price * item.quantity;
        const totalAfterDiscount = totalBeforeDiscount - (item.discount || 0);
        const pricePerUnit = totalAfterDiscount / item.quantity;
        
        const newItem: IInvoiceItemExtended = {
          ...item,
          quantity: 1,
          total: 1 * pricePerUnit,
          barcode: item.barcode || '',        // Ensure barcode is included
          barcodeId: item.barcodeId || ''     // Ensure barcodeId is included
        };
        const newReturnItems = [...returnItems, newItem];
        setReturnItems(newReturnItems);
        calculateTotals(newReturnItems);
      }
    } catch (error) {
      console.error('خطأ في إضافة المنتج للمرتجعات:', error);
      setError('حدث خطأ أثناء إضافة المنتج للمرتجعات');
    }
  };

  // Remove item from return list
  const removeItemFromReturn = (item: IInvoiceItemExtended, index: number) => {
    const newReturnItems = [...returnItems];
    
    if (newReturnItems[index].quantity > 1) {
      // If quantity is more than 1, just decrease it
      // حساب السعر الفعلي للوحدة بعد توزيع الخصم
      const sourceItem = sourceInvoiceItems.find(i =>
        i.productId === newReturnItems[index].productId &&
        i.barcode === newReturnItems[index].barcode &&
        i.price === newReturnItems[index].price &&
        i.discount === newReturnItems[index].discount
      );
      
      if (sourceItem) {
        // حساب السعر الفعلي للوحدة بعد توزيع الخصم
        // الخصم الآن دائماً قيمة ثابتة بعد التحويل في fetchSourceInvoice
        const totalBeforeDiscount = newReturnItems[index].price * sourceItem.quantity;
        const totalAfterDiscount = totalBeforeDiscount - (newReturnItems[index].discount || 0);
        const pricePerUnit = totalAfterDiscount / sourceItem.quantity;
        
        newReturnItems[index] = {
          ...newReturnItems[index],
          quantity: newReturnItems[index].quantity - 1,
          total: (newReturnItems[index].quantity - 1) * pricePerUnit
        };
      } else {
        // Fallback if source item not found
        newReturnItems[index] = {
          ...newReturnItems[index],
          quantity: newReturnItems[index].quantity - 1,
          total: (newReturnItems[index].quantity - 1) * newReturnItems[index].price
        };
      }
      setReturnItems(newReturnItems);
    } else {
      // Remove the item completely
      newReturnItems.splice(index, 1);
      setReturnItems(newReturnItems);
    }
    
    // Recalculate totals
    calculateTotals(newReturnItems);
  };

  // Calculate invoice totals
  const calculateTotals = (items: IInvoiceItemExtended[]) => {
    const subtotal = items.reduce((total, item) => total + (item.total || 0), 0);
    const taxAmount = items.reduce((total, item) => {
      if (!item.tax) return total;
      // يجب حساب الضريبة على الإجمالي الفعلي (بعد الخصم) وليس على السعر الأصلي
      const itemTotalWithTax = item.total || 0;
      const taxRate = item.tax / 100;
      const itemTax = itemTotalWithTax - (itemTotalWithTax / (1 + taxRate));
      return total + itemTax;
    }, 0);
    
    const discount = formData.discount || 0;
    let total = subtotal;
    
    if (discount > 0) {
      const subtotalNonService = items.reduce((total, item) => {
        return item.isServiceProduct ? total : total + (item.total || 0);
      }, 0);
      
      if (subtotalNonService > 0) {
        const discountFactor = Math.max(0, 1 - (discount / subtotalNonService));
        const nonServiceTotal = items.reduce((total, item) => {
          return item.isServiceProduct ? total : total + (item.total || 0) * discountFactor;
        }, 0);
        const serviceTotal = items.reduce((total, item) => {
          return item.isServiceProduct ? total + (item.total || 0) : total;
        }, 0);
        total = nonServiceTotal + serviceTotal;
      }
      // إذا كان subtotalNonService == 0، فلا يوجد خصم (جميع المنتجات خدمية)
    }
    
    setFormData(prev => ({
      ...prev,
      subtotal,
      tax: taxAmount,
      total
    }));
  };

  // Handle payment methods change
  const handlePaymentMethodsChange = (paymentMethods: IPaymentMethodUsage[]) => {
    setFormData(prev => ({
      ...prev,
      paymentMethods
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (returnItems.length === 0) {
      setError('يرجى إضافة منتج واحد على الأقل للإرجاع');
      return;
    }
    
    if (!sourceInvoice) {
      setError('يرجى تحديد فاتورة المصدر أولاً');
      return;
    }
    
    // Check if any product quantity exceeds the original invoice
    // Match by productId + barcode + price + discount to identify the exact row
    const validQuantities = returnItems.every(returnItem => {
      const matchingItems = sourceInvoiceItems.filter(item =>
        item.productId === returnItem.productId &&
        item.barcode === returnItem.barcode &&
        item.price === returnItem.price &&
        item.discount === returnItem.discount
      );
      const totalSourceQty = matchingItems.reduce((sum, item) => sum + item.quantity, 0);
      return matchingItems.length > 0 && returnItem.quantity <= totalSourceQty;
    });
    
    if (!validQuantities) {
      setError('لا يمكن إرجاع كمية أكثر من الكمية الموجودة في الفاتورة الأصلية');
      return;
    }
    
    try {
      setSaving(true);
      
      // Determine invoice status based on payment method
      let invoiceStatus: 'draft' | 'completed' = 'completed';
      let isPartialPayment = false;
      let cashPaid = 0;
      
      if (useAlternativePayment) {
        if (cashPaymentAmount > 0) {
          cashPaid = cashPaymentAmount;
          // If there's remaining amount, set status to draft (pending)
          const remainingAmount = formData.total - cashPaymentAmount;
          invoiceStatus = remainingAmount > 0 ? 'draft' : 'completed';
          isPartialPayment = true; // Always true when there's cash payment
        } else {
          // No cash payment, entire amount is pending
          invoiceStatus = 'draft';
          isPartialPayment = true;
        }
      }

      // Prepare invoice data using new structured fields
      const invoiceData: IInvoiceCreateExtended = {
        ...formData,
        items: returnItems,
        subtotal: formData.subtotal,
        tax: formData.tax,
        total: formData.total,
        notes: formData.notes || '', // Keep original notes separate
        paymentMethods: [], // Import invoices don't use payment methods
        status: invoiceStatus,
        sourceInvoiceId: sourceInvoice._id, // Store source invoice ID
        sourceInvoiceNumber: sourceInvoice.invoiceNumber, // Store source invoice number for display
        customerId: sourceInvoice.customerId, // نسخ معرف العميل من الفاتورة الأصلية
        customerName: sourceInvoice.customerName, // نسخ اسم العميل من الفاتورة الأصلية
        cashPaidAmount: cashPaid, // Store cash payment amount
        isPartialPayment: isPartialPayment // Flag for partial payment
      };
      
      // تشخيص البيانات قبل الإرسال
      console.log('=== تشخيص البيانات في الواجهة الأمامية ===');
      console.log('sourceInvoiceId:', invoiceData.sourceInvoiceId);
      console.log('sourceInvoiceNumber:', invoiceData.sourceInvoiceNumber);
      console.log('cashPaidAmount:', invoiceData.cashPaidAmount);
      console.log('isPartialPayment:', invoiceData.isPartialPayment);
      console.log('status:', invoiceData.status);
      console.log('useAlternativePayment:', useAlternativePayment);
      console.log('cashPaymentAmount:', cashPaymentAmount);
      
      // Call the save function from parent
      await onSave(invoiceData);
      
    } catch (err: any) {
      console.error('Error saving invoice:', err);
      console.error('Error response:', err.response);
      console.error('Error response data:', err.response?.data);
      
      // استخراج رسالة الخطأ من الاستجابة
      let errorMessage = 'حدث خطأ أثناء حفظ الفاتورة';
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
        console.log('Using server error message:', errorMessage);
      } else if (err.message) {
        errorMessage = err.message;
        console.log('Using error message:', errorMessage);
      }
      
      console.log('Setting error message:', errorMessage);
      setError(errorMessage);
    } finally {
      // تأكد من إيقاف حالة الحفظ في جميع الحالات
      setSaving(false);
    }
  };

  // Render loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%', bgcolor: '#f8f9fa', p: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          backgroundImage: 'linear-gradient(to right, #1976d2, #2196f3)',
          color: 'white'
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <ReturnIcon fontSize="large" />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 500 }}>
              {isEditing ? 'تعديل مرتجع فاتورة استيراد' : 'مرتجع فاتورة استيراد جديد'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: { xs: 2, sm: 0 } }}>
            <Button
              variant="contained"
              color="inherit"
              startIcon={<ArrowBackIcon />}
              onClick={onCancel}
              sx={{ 
                color: '#1976d2', 
                bgcolor: 'white',
                '&:hover': { bgcolor: '#e0e0e0' }
              }}
            >
              إلغاء
            </Button>
            
            {showPrintButton ? (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PrintIcon />}
                  onClick={onPrint}
                  sx={{
                    ml: 1,
                    bgcolor: '#2196f3',
                    '&:hover': { bgcolor: '#1976d2' }
                  }}
                >
                  طباعة
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(`/invoices/${pageId}`)}
                  sx={{
                    ml: 1,
                    bgcolor: '#4caf50',
                    '&:hover': { bgcolor: '#388e3c' }
                  }}
                >
                  العودة للقائمة
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={saving}
                sx={{
                  bgcolor: '#4caf50',
                  '&:hover': { bgcolor: '#388e3c' }
                }}
              >
                {saving ? <CircularProgress size={24} color="inherit" /> : 'حفظ المرتجع'}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      
      {/* Source Invoice Search */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 2,
          borderLeft: '4px solid #ff9800'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          mb: 2,
          color: '#ff9800'
        }}>
          <ReceiptIcon />
          <Typography variant="h6" sx={{ fontWeight: 500 }}>
            استيراد بيانات الفاتورة
          </Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="رقم الفاتورة"
              value={sourceInvoiceNumber}
              onChange={(e) => setSourceInvoiceNumber(e.target.value)}
              disabled={!!sourceInvoice || (isEditing ? true : false)}
              placeholder="أدخل رقم الفاتورة المراد استرجاع منتجاتها"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Button
              variant="contained"
              color="warning"
              startIcon={<SearchIcon />}
              onClick={() => fetchSourceInvoice(sourceInvoiceNumber)}
              disabled={!!sourceInvoice || !sourceInvoiceNumber || (isEditing ? true : false)}
              sx={{ height: '56px' }}
            >
              بحث عن الفاتورة
            </Button>
          </Grid>
        </Grid>
        
        {sourceInvoice && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
              تم استيراد بيانات الفاتورة رقم: {sourceInvoice.invoiceNumber}
            </Typography>
            <Typography variant="body2">
              التاريخ: {new Date(sourceInvoice.date).toLocaleDateString()}
            </Typography>
            <Typography variant="body2">
              إجمالي الفاتورة: {sourceInvoice.total.toFixed(2)} ريال
            </Typography>
            <Typography variant="body2">
              عدد المنتجات: {sourceInvoice.items.length}
            </Typography>
          </Box>
        )}
      </Paper>
      
      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Invoice Details and Totals */}
        <Grid item xs={12} md={4} sx={{ order: { xs: 2, md: 1 } }}>
          <Card 
            elevation={3} 
            sx={{ 
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardContent sx={{ pb: 0 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                mb: 2,
                color: '#1976d2'
              }}>
                <ReceiptIcon />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  تفاصيل مرتجع الفاتورة
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="رقم المرتجع"
                    name="invoiceNumber"
                    value={formData.invoiceNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                    required
                    disabled={isEditing ? true : (page?.lockInvoiceNumber || false)}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="التاريخ والوقت"
                    name="date"
                    type="datetime-local"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    required
                    disabled={page?.lockDate || false}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="ملاحظات"
                    name="notes"
                    value={formData.notes !== `SOURCE_INVOICE:${sourceInvoice?.invoiceNumber}` ? formData.notes : ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    multiline
                    rows={3}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useAlternativePayment}
                        onChange={(e) => {
                          setUseAlternativePayment(e.target.checked);
                          if (!e.target.checked) {
                            setCashPaymentAmount(0);
                          }
                        }}
                        color="primary"
                      />
                    }
                    label="دفع هذه الفاتورة بطريقة أخرى"
                  />
                  <Typography variant="caption" display="block" color="text.secondary">
                    عند اختيار هذا الخيار، لن يتم تسجيل المعاملة في حساب الصندوق وستبقى الفاتورة معلقة
                  </Typography>
                </Grid>

                {/* مربع الدفع النقدي الجزئي */}
                {useAlternativePayment && (
                  <Grid item xs={12}>
                    <Box sx={{
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      bgcolor: '#f9f9f9',
                      mt: 1
                    }}>
                      <Typography variant="subtitle2" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <MoneyIcon />
                          الدفع النقدي (اختياري)
                        </Box>
                      </Typography>
                      
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="المبلغ المدفوع نقدياً"
                            type="number"
                            value={cashPaymentAmount}
                            onChange={(e) => {
                              const amount = parseFloat(e.target.value) || 0;
                              if (amount >= 0 && amount <= formData.total) {
                                setCashPaymentAmount(amount);
                              }
                            }}
                            fullWidth
                            size="small"
                            InputProps={{
                              inputProps: {
                                min: 0,
                                max: formData.total,
                                step: 0.01
                              },
                              endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>ريال</Typography>
                            }}
                            helperText={`الحد الأقصى: ${formData.total.toFixed(2)} ريال`}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" color="text.secondary">
                              المبلغ المتبقي:
                            </Typography>
                            <Typography variant="h6" color="primary" fontWeight="bold">
                              {(formData.total - cashPaymentAmount).toFixed(2)} ريال
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {cashPaymentAmount > 0 ? 'سيبقى معلقاً' : 'سيبقى كاملاً معلقاً'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      
                      {cashPaymentAmount > 0 && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          سيتم خصم {cashPaymentAmount.toFixed(2)} ريال من حساب الصندوق، والمبلغ المتبقي {(formData.total - cashPaymentAmount).toFixed(2)} ريال سيبقى معلقاً
                        </Alert>
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
            
            {/* Totals Summary */}
            <Box flexGrow={1} display="flex" flexDirection="column" justifyContent="flex-end">
              <Divider />
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: '0 0 8px 8px' }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <ShoppingCartIcon />
                    ملخص المبالغ
                  </Box>
                </Typography>
                
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Typography variant="body1" color="text.secondary">المجموع الفرعي:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1" align="right" fontWeight="bold">
                      {formData.subtotal.toFixed(2)} ريال
                    </Typography>
                  </Grid>
                  
                  {page.enableTax && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body1" color="text.secondary">الضريبة:</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" align="right" fontWeight="bold">
                          {(formData.tax || 0).toFixed(2)} ريال
                        </Typography>
                      </Grid>
                    </>
                  )}
                  
                  <Grid item xs={6}>
                    <Typography variant="body1" color="text.secondary">الخصم:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      variant="outlined"
                      name="discount"
                      type="number"
                      size="small"
                      value={formData.discount || 0}
                      onChange={(e) => {
                        const discount = parseFloat(e.target.value);
                        setFormData(prev => {
                          const newFormData = { ...prev, discount };
                          const total = prev.subtotal - discount;
                          return { ...newFormData, total };
                        });
                      }}
                      disabled={page?.lockDiscount || false}
                      InputProps={{
                        inputProps: { min: 0 },
                        sx: { textAlign: 'right' }
                      }}
                      sx={{ width: '100%' }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="h6" color="primary" fontWeight="bold">الإجمالي:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6" align="right" color="primary" fontWeight="bold">
                      {formData.total.toFixed(2)} ريال
                    </Typography>
                  </Grid>
                </Grid>
                
                {/* Payment Methods Section */}
                {page && page.enablePaymentMethods && !useAlternativePayment && (
                  <Box mt={3}>
                    <PaymentMethodsSection
                      invoiceTotal={formData.total}
                      initialPaymentMethods={formData.paymentMethods || []}
                      onPaymentMethodsChange={handlePaymentMethodsChange}
                      disabled={saving}
                      cashAccountId={page.cashAccountId}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        </Grid>
        
        {/* Product Selection Area */}
        <Grid item xs={12} md={8} sx={{ order: { xs: 1, md: 2 } }}>
          <Grid container spacing={2}>
            {/* Source Invoice Products */}
            <Grid item xs={12} md={6}>
              <Card elevation={3} sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: '#ff9800',
                    mb: 2
                  }}>
                    <ReceiptIcon />
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      منتجات الفاتورة الأصلية ({sourceInvoiceItems.length})
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  {sourceInvoiceItems.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="body1">
                        لم يتم استيراد أي فاتورة بعد
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        قم بالبحث عن فاتورة باستخدام رقم الفاتورة أولاً
                      </Typography>
                    </Box>
                  ) : (
                    <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
                      {sourceInvoiceItems.map((item, index) => (
                        <ListItem
                          key={`source-${index}`}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              color="primary"
                              onClick={() => addItemToReturn(item)}
                              title="إضافة للمرتجعات"
                            >
                              <ArrowBackIcon />
                            </IconButton>
                          }
                          sx={{
                            mb: 1,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            '&:hover': { bgcolor: '#fff3e0' }
                          }}
                        >
                          <ListItemIcon>
                            <ShoppingCartIcon color="warning" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box>
                                <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                                  {item.productName}
                                </Typography>
                                {/* عرض خصائص المنتج */}
                                {((item as any).variantAttributes && Object.keys((item as any).variantAttributes).length > 0) && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      خصائص المنتج:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {Object.entries((item as any).variantAttributes).map(([key, value], idx) => (
                                        <Chip
                                          key={idx}
                                          label={`${key}: ${value}`}
                                          size="small"
                                          variant="outlined"
                                          color="primary"
                                          sx={{ fontSize: '0.7rem', height: '20px' }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            }
                            secondary={
                              <>
                                <Typography variant="body2" component="span">
                                  الكمية: {item.quantity} {item.unitName || ''}
                                </Typography>
                                <br />
                                <Typography variant="body2" component="span">
                                  السعر: {(() => {
                                    // حساب سعر الوحدة بعد توزيع الخصم الكلي على الكميات
                                    // الخصم الآن دائماً قيمة ثابتة بعد التحويل في fetchSourceInvoice
                                    const totalBeforeDiscount = item.price * item.quantity;
                                    const totalAfterDiscount = totalBeforeDiscount - (item.discount || 0);
                                    const pricePerUnit = totalAfterDiscount / item.quantity;
                                    return pricePerUnit.toFixed(2);
                                  })()} ريال
                                </Typography>
                                {item.barcode && (
                                  <>
                                    <br />
                                    <Typography variant="body2" component="span">
                                      الباركود: {item.barcode}
                                    </Typography>
                                  </>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            {/* Return Items */}
            <Grid item xs={12} md={6}>
              <Card elevation={3} sx={{ borderRadius: 2, height: '100%' }}>
                <CardContent>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 2
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: '#4caf50'
                    }}>
                      <ReturnIcon />
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        المنتجات المرتجعة ({returnItems.length})
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  {returnItems.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="body1">
                        لم يتم إضافة أي منتجات للإرجاع بعد
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        اختر المنتجات من القائمة على اليمين لإضافتها للمرتجعات
                      </Typography>
                    </Box>
                  ) : (
                    <List sx={{ maxHeight: '400px', overflow: 'auto' }}>
                      {returnItems.map((item, index) => (
                        <ListItem
                          key={`return-${index}`}
                          secondaryAction={
                            <IconButton 
                              edge="end" 
                              color="error"
                              onClick={() => removeItemFromReturn(item, index)}
                              title="إزالة من المرتجعات"
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                          sx={{
                            mb: 1,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            '&:hover': { bgcolor: '#e8f5e9' }
                          }}
                        >
                          <ListItemIcon>
                            <ReturnIcon color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box>
                                <Typography variant="body1" component="div" sx={{ fontWeight: 'medium' }}>
                                  {item.productName}
                                </Typography>
                                {/* عرض خصائص المنتج */}
                                {((item as any).variantAttributes && Object.keys((item as any).variantAttributes).length > 0) && (
                                  <Box sx={{ mt: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      خصائص المنتج:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {Object.entries((item as any).variantAttributes).map(([key, value], idx) => (
                                        <Chip
                                          key={idx}
                                          label={`${key}: ${value}`}
                                          size="small"
                                          variant="outlined"
                                          color="success"
                                          sx={{ fontSize: '0.7rem', height: '20px' }}
                                        />
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            }
                            secondary={
                              <>
                                <Typography variant="body2" component="span">
                                  الكمية: {item.quantity} {item.unitName || ''}
                                </Typography>
                                <br />
                                <Typography variant="body2" component="span">
                                  السعر: {(() => {
                                    // حساب سعر الوحدة بعد توزيع الخصم الكلي على الكميات الأصلية
                                    // يجب استخدام الكمية من الفاتورة الأصلية وليس الكمية المرتجعة
                                    // الخصم الآن دائماً قيمة ثابتة بعد التحويل في fetchSourceInvoice
                                    const sourceItem = sourceInvoiceItems.find(i =>
                                      i.productId === item.productId &&
                                      i.barcode === item.barcode &&
                                      i.price === item.price &&
                                      i.discount === item.discount
                                    );
                                    
                                    if (sourceItem) {
                                      const totalBeforeDiscount = item.price * sourceItem.quantity;
                                      const totalAfterDiscount = totalBeforeDiscount - (item.discount || 0);
                                      const pricePerUnit = totalAfterDiscount / sourceItem.quantity;
                                      return pricePerUnit.toFixed(2);
                                    }
                                    
                                    // Fallback: إذا لم نجد المنتج في الفاتورة الأصلية
                                    return item.price.toFixed(2);
                                  })()} ريال
                                </Typography>
                                <br />
                                <Typography variant="body2" component="span">
                                  الإجمالي: {item.total.toFixed(2)} ريال
                                </Typography>
                                {item.barcode && (
                                  <>
                                    <br />
                                    <Typography variant="body2" component="span">
                                      الباركود: {item.barcode}
                                    </Typography>
                                  </>
                                )}
                              </>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      
      {/* Payment Methods Dialog */}
      <Dialog
        open={showPaymentMethodsDialog}
        onClose={() => setShowPaymentMethodsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          bgcolor: '#1976d2',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <InfoIcon />
          <Typography variant="h6">تنبيه: طرق الدفع المستوردة</Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            تم استيراد طرق الدفع من الفاتورة الأصلية
          </Alert>
          
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
            طرق الدفع المستوردة:
          </Typography>
          
          {importedPaymentMethods.length > 0 ? (
            <List>
              {importedPaymentMethods.map((method, index) => (
                <ListItem
                  key={index}
                  sx={{
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: '#f5f5f5'
                  }}
                >
                  <ListItemIcon>
                    <PaymentIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {method.name}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          المبلغ: {method.amount.toFixed(2)} ريال
                        </Typography>
                        {method.totalFees > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            الرسوم: {method.totalFees.toFixed(2)} ريال
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              لا توجد طرق دفع في الفاتورة الأصلية
            </Typography>
          )}
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            يمكنك تعديل أو حذف طرق الدفع من قسم "طرق الدفع" في تفاصيل الفاتورة
          </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setShowPaymentMethodsDialog(false)}
            variant="contained"
            color="primary"
            fullWidth
          >
            فهمت
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
export default ImportInvoiceReturnForm;